import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styles from './diary-register-absences.module.css';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import ErrorComponent from '../../components/error/error';
import Loading from '../../components/loading/loading';
import StudentSelector from '../../components/student-selector/student-selector';
import SubmitButton from '../../components/submit-button/submit-button';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import AbsenceService from '../../services/absence-service';
import DiaryService from '../../services/diary-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

// Helpers
import { getTodayDateString, isAbsenceDateValid } from '../../helpers/absence-helper';
import {
  SELECTOR_CLASSROOM_ID,
  dayTakenMessage,
  discardDraftErrorMessage,
  draftDiscardPrompt,
  selectedStudents as studentsOf,
  staffNamesOf,
  takenDays,
  toSelectorClassroom,
} from '../../helpers/daily-records-helper';

const DiaryRegisterAbsences = () => {
  const { classroomUuid } = useParams<{ classroomUuid: string }>();
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);

  const [error, setError] = useState(false);
  const [absenceService, setAbsenceService] = useState<AbsenceService | null>(null);
  const [diaryService, setDiaryService] = useState<DiaryService | null>(null);
  const [classroomList, setClassroomList] = useState<ClassroomStudents[] | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [absenceDate, setAbsenceDate] = useState(getTodayDateString());
  const [allowSubmit, setAllowSubmit] = useState(false);

  useEffect(() => {
    const schoolId = new StorageService().getEmployee().schoolId;

    new SchoolService()
      .getSchoolById(schoolId)
      .then((school) => {
        setAbsenceService(new AbsenceService(school.uuid));
        setDiaryService(new DiaryService(school.uuid));
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Só os alunos cujo dia ainda está livre (sem diário nem falta) podem receber falta.
  useEffect(() => {
    if (!diaryService || !isAbsenceDateValid(absenceDate)) return;

    diaryService
      .listPendingStudents(classroomUuid, absenceDate)
      .then((pending) => {
        setClassroomList([toSelectorClassroom(pending)]);
        setSelectedStudents([]);
      })
      .catch(() => setError(true));
  }, [diaryService, classroomUuid, absenceDate]);

  // Validação
  useEffect(() => {
    const dateValid = absenceDate && isAbsenceDateValid(absenceDate);
    const studentsValid = selectedStudents.length > 0;

    setAllowSubmit(dateValid && studentsValid);
  }, [absenceDate, selectedStudents]);

  const handleStudentChange = (
    selectedClassrooms: number[],
    selectedStudents: number[]
  ) => {
    setSelectedStudents(selectedStudents);
  };

  const handleSubmit = () => {
    const students = studentsOf(classroomList[0], selectedStudents);
    const data: RegisterAbsencesRequest = {
      classroomUuid,
      studentUuids: students.map((student) => student.uuid),
      date: absenceDate,
    };

    const studentNames = Object.fromEntries(students.map((student) => [student.uuid, student.name]));
    const registered = () => {
      setToastContent({
        isOpen: true,
        text: `${selectedStudents.length} falta(s) registrada(s) com sucesso!`,
        type: 'success',
      });
      navigate(`/diarios/turma/${classroomUuid}`);
    };

    // Um aluno num rascunho: oferece tirá-lo do rascunho e registrar a falta de uma vez, com a mesma
    // requisição e discardDraft. Os nomes dos autores de rascunho vêm dos grupos do dia, numa chamada só.
    const offerDiscard = (prompt: string, staffNames: Record<string, string>) =>
      setModalContent({
        title: 'Aluno em rascunho',
        modalContent: <p>{prompt}</p>,
        submitButtonTitle: 'remover e registrar a falta',
        submitButtonType: 'primary',
        submitData: null,
        onSubmitButtonClick: () =>
          absenceService
            .registerAbsences({ ...data, discardDraft: true })
            .then(registered)
            .catch((err) => {
              const text = discardDraftErrorMessage(err, studentNames, staffNames) || 'Houve um erro ao registrar as faltas.';
              // Fora do alcance, repetir daria o mesmo 403: fecha o modal em vez de deixar o botão ativo.
              if (err?.status === 403) {
                setToastContent({ isOpen: true, text, type: 'error' });
                return;
              }

              return Promise.reject(text);
            }),
        isOpen: true,
      });

    absenceService
      .registerAbsences(data)
      .then(registered)
      .catch(async (err) => {
        const draftInTheWay = takenDays(err)?.some((day) => day.heldBy === 'DiaryDraft');
        const staffNames: Record<string, string> = draftInTheWay
          ? await diaryService
              .listDiaryGroups(classroomUuid, absenceDate)
              .then(staffNamesOf)
              .catch(() => ({}))
          : {};
        const prompt = draftDiscardPrompt(err, studentNames, staffNames);
        if (prompt) {
          offerDiscard(prompt, staffNames);
          return;
        }

        setToastContent({
          isOpen: true,
          text: dayTakenMessage(err, studentNames, staffNames) || 'Houve um erro ao registrar as faltas.',
          type: 'error',
        });
      });
  };

  if (error) return <ErrorComponent type='contentFull' />;
  if (!classroomList) return <Loading type='contentFull' />;

  const getTitle = () => {
    if (selectedStudents.length === 0) return 'Selecione os alunos ausentes';
    if (selectedStudents.length === 1) {
      const student = classroomList[0].students.find(s => s.id === selectedStudents[0]);
      return `${student?.name || 'Aluno'} - Ausente`;
    }
    return `${selectedStudents.length} alunos selecionados`;
  };

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.doubleWithActionButtons}
      biggerSide={BaseContentContainerBiggerSide.right}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      {/* Formulário - Lado Esquerdo */}
      <div className={styles.formContainer}>
        <h2>{getTitle()}</h2>

        <div className={styles.fieldContainer}>
          <label htmlFor="absenceDate">
            Data da Falta:
          </label>
          <input
            type="date"
            id="absenceDate"
            value={absenceDate}
            max={getTodayDateString()}
            onChange={(e) => setAbsenceDate(e.target.value)}
          />
          {absenceDate && !isAbsenceDateValid(absenceDate) && (
            <p className={styles.warningText}>
              ⚠️ Não é possível registrar faltas em datas futuras
            </p>
          )}
        </div>

      </div>

      {/* StudentSelector - Lado Direito */}
      <StudentSelector
        classrooms={classroomList}
        handleStudentChange={handleStudentChange}
        lockClassroomSelection={true}
        initialClassrooms={[SELECTOR_CLASSROOM_ID]}
      />

      {/* Botões */}
      <ButtonsBar pattern='together' sideAlign='right' verticalAlign='bottom'>
        <Link to={`/diarios/turma/${classroomUuid}`}>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='registrar faltas'
          variant='primary'
          onClick={handleSubmit}
          disabled={!allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default DiaryRegisterAbsences;
